/**
 * Operator-facing labels for the compliance controller's CBF (control barrier
 * function) constraints. Single source of truth for the wording shared by:
 *   - the live in-session teleop toasts (`useTeleopCBFWarnings.ts`, which
 *     builds them from CBFState lambda indices), and
 *   - the teleop-start gate (`complianceCBF.ts`, which builds them from barrier
 *     names).
 *
 * The two callers have different inputs (lambda indices vs. barrier names) so
 * they don't share the mapping — they share these constructors, so a wording
 * change lands in one place instead of drifting between the two surfaces.
 */

export type CBFAxis = 'X' | 'Y' | 'Z';
export type CBFSign = '+' | '−';

/**
 * EE-containment index (0-5) → signed axis, matching the controller's published
 * layout `[max_x, max_y, max_z, min_x, min_y, min_z]`.
 */
export const EE_AXIS_BY_INDEX: ReadonlyArray<{ axis: CBFAxis; sign: CBFSign }> =
  [
    { axis: 'X', sign: '+' },
    { axis: 'Y', sign: '+' },
    { axis: 'Z', sign: '+' },
    { axis: 'X', sign: '−' },
    { axis: 'Y', sign: '−' },
    { axis: 'Z', sign: '−' },
  ];

export function eeBoundaryLabel(sign: CBFSign, axis: CBFAxis): string {
  return `End Effector ${sign}${axis} Boundary`;
}

export function jointLimitLabel(
  joint: number,
  bound: 'upper' | 'lower',
): string {
  return `Joint ${joint} ${bound === 'upper' ? 'Upper' : 'Lower'} Limit`;
}

export function wholeBodyBoundaryLabel(sign: CBFSign, axis: CBFAxis): string {
  return `Whole-Body ${sign}${axis} Boundary`;
}

export const SINGULARITY_LABEL = 'Singularity';
export const EXTERNAL_COLLISION_LABEL = 'External Collision';
export const SELF_COLLISION_LABEL = 'Self-Collision';
export const FORCE_LIMIT_LABEL = 'Force Limit';
